// This deployment serves the React SPA at the MSAL redirectUri (the site origin).
// When a popup sign-in returns, Microsoft loads that redirectUri INSIDE the popup,
// which would otherwise boot the whole SPA and call handleRedirectPromise() in the
// wrong window — where the originating request was never cached — throwing
// no_token_request_cache_error (the error shown inside the popup).
//
// MSAL v5 ships a dedicated entry point for redirect landing pages:
// broadcastResponseToMainFrame(). For popup/SSO it posts the raw response to the
// opener over a BroadcastChannel and closes the window; for a full-page redirect it
// caches the response and navigates back to the app. In every case the landing page
// must run THAT, not the SPA. We detect an auth response in the URL and hand off.

const authResponseInUrl = /[#?&](code|error|error_description|state|id_token|access_token|session_state)=/i;

export function hasAuthResponseInUrl(location: Pick<Location, 'hash' | 'search'> = window.location): boolean {
  return authResponseInUrl.test(location.hash) || authResponseInUrl.test(location.search);
}

export async function completeAuthRedirectIfPresent(
  location: Pick<Location, 'hash' | 'search'> = window.location,
): Promise<boolean> {
  if (!hasAuthResponseInUrl(location)) return false;
  try {
    const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');
    await broadcastResponseToMainFrame();
    // Popup/SSO: response broadcast to the opener and window.close() attempted.
    // Redirect: response cached and navigation to the app started.
    // Either way the SPA must not boot on this page load.
    return true;
  } catch {
    // The URL was not a genuine MSAL response (or the bridge could not run).
    // Fall through so the SPA boots normally.
    return false;
  }
}
